import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";

const SITE = "https://qrmenu-pro.com";
const SUPPORTED = ["de", "en", "tr"];
const HREFLANG_MAP = { de: "de-DE", en: "en-US", tr: "tr-TR" };
const OG_LOCALE_MAP = { de: "de_DE", en: "en_US", tr: "tr_TR" };

function upsertMeta(name, content, attr = "name") {
  if (!content) return;
  let m = document.querySelector(`meta[${attr}="${name}"]`);
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute(attr, name);
    document.head.appendChild(m);
  }
  m.setAttribute("content", content);
}

function removeAllAlternates() {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
}

function upsertCanonical(href) {
  let l = document.querySelector('link[rel="canonical"]');
  if (!l) {
    l = document.createElement("link");
    l.setAttribute("rel", "canonical");
    document.head.appendChild(l);
  }
  l.setAttribute("href", href);
}

function appendAlternate(hreflang, href) {
  const l = document.createElement("link");
  l.setAttribute("rel", "alternate");
  l.setAttribute("hreflang", hreflang);
  l.setAttribute("href", href);
  document.head.appendChild(l);
}

function upsertJsonLd(id, json) {
  let s = document.getElementById(id);
  if (!s) {
    s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = id;
    document.head.appendChild(s);
  }
  s.textContent = JSON.stringify(json);
}

/**
 * Strip the leading /de, /en, /tr (if any) and return the inner pathname.
 * "/" → "/", "/en" → "/", "/en/pricing" → "/pricing"
 */
function stripLangPrefix(path) {
  const parts = path.split("/").filter(Boolean);
  if (parts.length && SUPPORTED.includes(parts[0])) {
    const inner = "/" + parts.slice(1).join("/");
    return inner === "/" ? "/" : inner;
  }
  return path || "/";
}

/**
 * Build the localized URL for a given language and inner path.
 *   de + "/"       → SITE + "/de"
 *   en + "/pricing" → SITE + "/en/pricing"
 *   x-default ("/" + "/pricing") → SITE + "/pricing"
 */
function buildLocaleUrl(lang, innerPath) {
  const suffix = innerPath === "/" ? "" : innerPath;
  if (!lang) return `${SITE}${suffix || "/"}`;
  return `${SITE}/${lang}${suffix}`;
}

/**
 * Sets <title>, meta description/keywords, OG + Twitter tags, hreflang alternates,
 * a self-referencing canonical, and optional JSON-LD structured data per page.
 *
 * Usage:
 *   useLocalizedSEO({
 *     titleByLang:   { de, en, tr },
 *     descByLang:    { de, en, tr },
 *     keywordsByLang:{ de, en, tr },
 *     jsonLdByLang:  { de: {...}, en: {...}, tr: {...} },  // optional
 *     ogImage: "https://.../og.png",                       // optional
 *   });
 */
export default function useLocalizedSEO({
  titleByLang,
  descByLang,
  keywordsByLang,
  jsonLdByLang,
  ogImage,
  pathByLang,
} = {}) {
  const { lang: contextLang } = useLang();
  const loc = useLocation();

  useEffect(() => {
    const path = loc.pathname || "/";
    const innerPath = stripLangPrefix(path);

    // ----- Resolve the EFFECTIVE language for SEO output -----
    // The URL is the single source of truth for SEO purposes — a page must always
    // self-canonicalise to its URL identity regardless of any persisted user lang
    // preference.
    //   /de/* → de, /en/* → en, /tr/* → tr
    //   Flat single-lang pages (e.g. /qr-menu-london) → look up via pathByLang
    //   everything else → de (x-default)
    const firstSeg = path.split("/").filter(Boolean)[0] || "";
    const hasPrefix = SUPPORTED.includes(firstSeg);
    let lang;
    if (hasPrefix) {
      lang = firstSeg;
    } else if (pathByLang) {
      // Find the lang whose pathByLang entry matches the current pathname.
      const match = SUPPORTED.find((l) => pathByLang[l] === path);
      lang = match || "de";
    } else {
      lang = "de";
    }

    const title = (titleByLang && titleByLang[lang]) || titleByLang?.de;
    const desc = (descByLang && descByLang[lang]) || descByLang?.de;
    const keywords = (keywordsByLang && keywordsByLang[lang]) || keywordsByLang?.de;

    if (title) document.title = title;
    if (desc) {
      upsertMeta("description", desc);
      upsertMeta("og:description", desc, "property");
      upsertMeta("twitter:description", desc);
    }
    if (keywords) upsertMeta("keywords", keywords);
    if (title) {
      upsertMeta("og:title", title, "property");
      upsertMeta("twitter:title", title);
    }
    upsertMeta("og:type", "website", "property");
    upsertMeta("og:site_name", "qrmenu-pro.com", "property");
    upsertMeta("og:locale", OG_LOCALE_MAP[lang] || "de_DE", "property");
    upsertMeta("twitter:card", "summary_large_image");
    if (ogImage) {
      upsertMeta("og:image", ogImage, "property");
      upsertMeta("twitter:image", ogImage);
    }
    upsertMeta("robots", "index, follow");

    // --- Canonical: self-referencing ---
    // When pathByLang is supplied (e.g. city pages where each lang has a different slug),
    // use those mappings instead of the generic prefix swap.

    let canonicalUrl;
    if (pathByLang && pathByLang[lang]) {
      canonicalUrl = `${SITE}${pathByLang[lang]}`;
    } else if (hasPrefix) {
      canonicalUrl = buildLocaleUrl(firstSeg, innerPath);
    } else {
      canonicalUrl = `${SITE}${innerPath === "/" ? "/" : innerPath}`;
    }
    upsertCanonical(canonicalUrl);
    upsertMeta("og:url", canonicalUrl, "property");

    // --- Hreflang: full symmetric group + x-default ---
    removeAllAlternates();
    SUPPORTED.forEach((l) => {
      const href = pathByLang && pathByLang[l]
        ? `${SITE}${pathByLang[l]}`
        : buildLocaleUrl(l, innerPath);
      appendAlternate(HREFLANG_MAP[l], href);
    });
    const xDefault = pathByLang && pathByLang.de
      ? `${SITE}${pathByLang.de}`
      : `${SITE}${innerPath === "/" ? "/" : innerPath}`;
    appendAlternate("x-default", xDefault);

    // --- HTML lang attribute ---
    document.documentElement.setAttribute("lang", lang);

    // Keep React context lang in sync with URL lang for unprefixed pages —
    // this prevents Navbar/Footer copy from being out of sync with the canonical URL.
    if (contextLang !== lang && typeof window !== "undefined") {
      // Soft sync — read-only via this hook would create a render loop if we called
      // setLang here. Instead we just emit a one-shot DOM event the LanguageContext
      // can opt into; for now the URL-derived 'lang' is the authoritative SEO source
      // and the visible UI catches up via LangRouteSync on next navigation.
    }

    // --- JSON-LD (optional, per language) ---
    if (jsonLdByLang) {
      const ld = jsonLdByLang[lang] || jsonLdByLang.de;
      if (ld) {
        if (Array.isArray(ld)) {
          ld.forEach((doc, i) => upsertJsonLd(`qrmp-jsonld-${i}`, doc));
        } else {
          upsertJsonLd("qrmp-jsonld-0", ld);
        }
      }
    }
  }, [contextLang, loc.pathname, titleByLang, descByLang, keywordsByLang, jsonLdByLang, ogImage, pathByLang]);
}

/**
 * Returns a helper that prefixes the current language to an in-app path,
 * so internal `<Link to={loc("/pricing")}>` keeps "/de/pricing" sticky.
 *
 *   const loc = useLocalizedHref();
 *   <Link to={loc("/pricing")}>...</Link>
 *
 * For non-localized routes (login, register, dashboard, /m/, …) pass `{exact: true}`.
 */
export function useLocalizedHref() {
  const location = useLocation();
  const seg = location.pathname.split("/").filter(Boolean)[0] || "";
  const activeLang = SUPPORTED.includes(seg) ? seg : null;
  return (path, opts = {}) => {
    if (!path || !path.startsWith("/")) return path;
    if (opts.exact || !activeLang) return path;
    // Do not prefix dashboard / m / login / register / payment paths
    const NON_LOCALIZED = ["/dashboard", "/m/", "/login", "/register", "/payment", "/forgot-password", "/reset-password"];
    if (NON_LOCALIZED.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p))) {
      return path;
    }
    return `/${activeLang}${path === "/" ? "" : path}`;
  };
}
